<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h2 class="page-title">{{ t('shortlist') }}</h2>
                <p class="page-subtitle">{{ t('shortlist_subtitle') }}</p>
            </div>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="listType" class="status-select" :disabled="loading">
                        <option value="all">{{ t('all') }}</option>
                        <option value="saved">{{ t('saved') }}</option>
                        <option value="short">{{ t('short') }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <div class="columns-wrap">
                        <button type="button" class="tool-btn tool-btn-light" @click="toggleColumns" :disabled="loading">COLUMNS</button>
                        <div v-if="showColumns" class="columns-dropdown">
                            <label v-for="c in columns" :key="c.key" class="columns-item">
                                <input type="checkbox" v-model="visibleColumns[c.key]" :disabled="c.locked">
                                <span>{{ t(c.labelKey) }}</span>
                            </label>
                        </div>
                    </div>

                    <button type="button" class="tool-btn tool-btn-primary" @click="load" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button type="button" class="tool-btn tool-btn-success" @click="openCreate" :disabled="loading">{{ t('create') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search') + '...'" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th v-if="visibleColumns.id">
                                <input v-if="showFilters" v-model="colFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer">
                                <input v-if="showFilters" v-model="colFilters.customer" type="text" class="th-input" :placeholder="t('customer')">
                                <span v-else>{{ t('customer') }}</span>
                            </th>
                            <th v-if="visibleColumns.customer_id">
                                <input v-if="showFilters" v-model="colFilters.customer_id" type="text" class="th-input" :placeholder="t('customer_id')">
                                <span v-else>{{ t('customer_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.active_plan_id">
                                <input v-if="showFilters" v-model="colFilters.active_plan_id" type="text" class="th-input" :placeholder="t('customer_active_plan_id')">
                                <span v-else>{{ t('customer_active_plan_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.plan_id">
                                <input v-if="showFilters" v-model="colFilters.plan_id" type="text" class="th-input" :placeholder="t('plan_id')">
                                <span v-else>{{ t('plan_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.notice_id">
                                <input v-if="showFilters" v-model="colFilters.notice_id" type="text" class="th-input" :placeholder="t('to_let_notice_id')">
                                <span v-else>{{ t('to_let_notice_id') }}</span>
                            </th>
                            <th v-if="visibleColumns.summary">
                                <input v-if="showFilters" v-model="colFilters.summary" type="text" class="th-input" :placeholder="t('summary')">
                                <span v-else>{{ t('summary') }}</span>
                            </th>
                            <th v-if="visibleColumns.list_type">
                                <select v-if="showFilters" v-model="colFilters.list_type" class="th-select">
                                    <option value="">{{ t('list_type') }}</option>
                                    <option value="saved">{{ t('saved') }}</option>
                                    <option value="short">{{ t('short') }}</option>
                                </select>
                                <span v-else>{{ t('list_type') }}</span>
                            </th>
                            <th v-if="visibleColumns.actions">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td :colspan="visibleColumnCount" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredItems" v-if="!loading" :key="row.ToLetShortListID">
                            <td v-if="visibleColumns.id" class="mono">{{ row.ToLetShortListID }}</td>
                            <td v-if="visibleColumns.customer">
                                <div class="cell-main">{{ row.CustomerName || '-' }}</div>
                                <div class="cell-sub mono">{{ row.MobileNo || '-' }}</div>
                            </td>
                            <td v-if="visibleColumns.customer_id" class="mono">{{ row.CustomerID ?? '-' }}</td>
                            <td v-if="visibleColumns.active_plan_id" class="mono">{{ row.CustomerActivePlanID ?? '-' }}</td>
                            <td v-if="visibleColumns.plan_id" class="mono">{{ row.PlanID ?? '-' }}</td>
                            <td v-if="visibleColumns.notice_id" class="mono">{{ row.ToLetNoticeID ?? '-' }}</td>
                            <td v-if="visibleColumns.summary">
                                <div class="cell-main">{{ row.DisplaySummary || '-' }}</div>
                                <div class="cell-sub mono">{{ row.Rent ? `Rent: ${row.Rent}` : '-' }}</div>
                            </td>
                            <td v-if="visibleColumns.list_type">
                                <span :class="['tag', listTypeFromRow(row) === 'saved' ? 'tag-blue' : 'tag-amber']">
                                    {{ listTypeFromRow(row) === 'saved' ? t('saved') : t('short') }}
                                </span>
                            </td>
                            <td v-if="visibleColumns.actions">
                                <div class="action-buttons">
                                    <button
                                        type="button"
                                        class="icon-btn icon-btn-light"
                                        @click="setListType(row, 'saved')"
                                        :disabled="busyIds[`type_${row.ToLetShortListID}`]"
                                        :title="t('make_saved')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M19 21L12 17L5 21V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V21Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                                        </svg>
                                    </button>

                                    <button
                                        type="button"
                                        class="icon-btn icon-btn-primary"
                                        @click="setListType(row, 'short')"
                                        :disabled="busyIds[`type_${row.ToLetShortListID}`]"
                                        :title="t('make_short')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2L15 9H22L16.5 13.5L18.5 21L12 16.8L5.5 21L7.5 13.5L2 9H9L12 2Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                                        </svg>
                                    </button>

                                    <button
                                        type="button"
                                        class="icon-btn icon-btn-danger"
                                        @click="remove(row)"
                                        :disabled="busyIds[`del_${row.ToLetShortListID}`]"
                                        :title="t('delete')"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                            <path d="M15.8333 5V16.6667C15.8333 17.5872 15.0872 18.3333 14.1667 18.3333H5.83333C4.91286 18.3333 4.16667 17.5872 4.16667 16.6667V5M12.5 5V3.33333C12.5 2.41286 11.7538 1.66667 10.8333 1.66667H9.16667C8.24619 1.66667 7.5 2.41286 7.5 3.33333V5M2.5 5H17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="!loading && filteredItems.length === 0">
                            <td :colspan="visibleColumnCount" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager">
                <div class="pager-left">{{ showingText }}</div>
                <div class="pager-right">
                    <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                    <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                    <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">{{ p }}</button>
                    <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                    <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                </div>
            </div>
        </div>

        <div v-if="showFormModal" class="modal-overlay" @click="closeForm">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>{{ t('create') }}</h3>
                    <button type="button" class="close-btn" @click="closeForm">
                        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="form-grid">
                        <label class="form-field">
                            <span class="form-label">{{ t('customer_id') }}</span>
                            <input v-model="form.customer_id" type="number" class="form-input" min="1">
                        </label>
                        <label class="form-field">
                            <span class="form-label">{{ t('customer_active_plan_id') }}</span>
                            <input v-model="form.customer_active_plan_id" type="number" class="form-input" min="1">
                        </label>
                        <label class="form-field">
                            <span class="form-label">{{ t('to_let_notice_id') }}</span>
                            <input v-model="form.to_let_notice_id" type="number" class="form-input" min="1">
                        </label>
                        <label class="form-field">
                            <span class="form-label">{{ t('list_type') }}</span>
                            <select v-model="form.list_type" class="form-input">
                                <option value="saved">{{ t('saved') }}</option>
                                <option value="short">{{ t('short') }}</option>
                            </select>
                        </label>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="tool-btn tool-btn-light" @click="closeForm" :disabled="saving">{{ t('cancel') }}</button>
                    <button type="button" class="tool-btn tool-btn-primary" @click="save" :disabled="saving">{{ saving ? t('saving') : t('save') }}</button>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-icon">
                            <svg v-if="toast.type === 'success'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </div>
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';

export default {
    name: 'RentSeekerShortlistPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));
        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const loading = ref(true);
        const saving = ref(false);
        const busyIds = ref({});

        const listType = ref('all');
        const searchQuery = ref('');
        const page = ref(1);
        const perPage = ref(20);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        const items = ref([]);
        let searchTimer = null;

        const showFilters = ref(true);
        const showColumns = ref(false);
        const columns = [
            { key: 'id', labelKey: 'id', locked: false },
            { key: 'customer', labelKey: 'customer', locked: false },
            { key: 'customer_id', labelKey: 'customer_id', locked: false },
            { key: 'active_plan_id', labelKey: 'customer_active_plan_id', locked: false },
            { key: 'plan_id', labelKey: 'plan_id', locked: false },
            { key: 'notice_id', labelKey: 'to_let_notice_id', locked: false },
            { key: 'summary', labelKey: 'summary', locked: false },
            { key: 'list_type', labelKey: 'list_type', locked: false },
            { key: 'actions', labelKey: 'actions', locked: true },
        ];
        const visibleColumns = ref({
            id: true,
            customer: true,
            customer_id: true,
            active_plan_id: true,
            plan_id: true,
            notice_id: true,
            summary: true,
            list_type: true,
            actions: true,
        });
        const colFilters = ref({
            id: '',
            customer: '',
            customer_id: '',
            active_plan_id: '',
            plan_id: '',
            notice_id: '',
            summary: '',
            list_type: '',
        });

        const visibleColumnCount = computed(() => {
            const v = visibleColumns.value || {};
            return columns.reduce((sum, c) => sum + (v[c.key] ? 1 : 0), 0) || 1;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((listType.value || 'all') !== 'all') return true;
            const f = colFilters.value || {};
            return Object.values(f).some((x) => String(x || '').trim() !== '');
        });

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const maxBtns = 5;
            let start = Math.max(1, current - Math.floor(maxBtns / 2));
            let end = Math.min(total, start + maxBtns - 1);
            start = Math.max(1, end - maxBtns + 1);
            const arr = [];
            for (let i = start; i <= end; i++) arr.push(i);
            return arr;
        });

        const normalize = (v) => (v ?? '').toString().toLowerCase();
        const listTypeFromRow = (row) => (row.IsSavedList ? 'saved' : 'short');

        const filteredItems = computed(() => {
            const list = Array.isArray(items.value) ? items.value : [];
            const f = colFilters.value || {};
            const idQ = normalize(f.id).trim();
            const customerQ = normalize(f.customer).trim();
            const customerIdQ = normalize(f.customer_id).trim();
            const activePlanQ = normalize(f.active_plan_id).trim();
            const planQ = normalize(f.plan_id).trim();
            const noticeQ = normalize(f.notice_id).trim();
            const summaryQ = normalize(f.summary).trim();
            const typeQ = (f.list_type || '').toString().trim().toLowerCase();

            if (!idQ && !customerQ && !customerIdQ && !activePlanQ && !planQ && !noticeQ && !summaryQ && !typeQ) return list;

            return list.filter((row) => {
                const id = normalize(row.ToLetShortListID);
                const customer = normalize(row.CustomerName) + ' ' + normalize(row.MobileNo);
                const customerId = normalize(row.CustomerID);
                const activePlan = normalize(row.CustomerActivePlanID);
                const planId = normalize(row.PlanID);
                const noticeId = normalize(row.ToLetNoticeID);
                const summary = normalize(row.DisplaySummary);
                const lt = listTypeFromRow(row);

                if (idQ && !id.includes(idQ)) return false;
                if (customerQ && !customer.includes(customerQ)) return false;
                if (customerIdQ && !customerId.includes(customerIdQ)) return false;
                if (activePlanQ && !activePlan.includes(activePlanQ)) return false;
                if (planQ && !planId.includes(planQ)) return false;
                if (noticeQ && !noticeId.includes(noticeQ)) return false;
                if (summaryQ && !summary.includes(summaryQ)) return false;
                if (typeQ && typeQ !== lt) return false;
                return true;
            });
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;
            if (!hasAnyFilter.value) return base;
            return `${base} (filtered: ${filteredItems.value.length})`;
        });

        const load = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/rent-seeker/shortlist', {
                    params: {
                        list_type: listType.value,
                        q: (searchQuery.value || '').trim(),
                        page: page.value,
                        per_page: perPage.value,
                        lang: langValue(),
                    },
                });
                items.value = res.data?.data?.items || [];
                pagination.value = res.data?.data?.pagination || { page: 1, per_page: 20, total: 0 };
            } finally {
                loading.value = false;
            }
        };

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(async () => {
                page.value = 1;
                await load();
            }, 320);
        };

        watch(listType, async () => {
            page.value = 1;
            await load();
        });

        watch(perPage, async () => {
            page.value = 1;
            await load();
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };
        const toggleColumns = () => {
            showColumns.value = !showColumns.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            listType.value = 'all';
            colFilters.value = { id: '', customer: '', customer_id: '', active_plan_id: '', plan_id: '', notice_id: '', summary: '', list_type: '' };
            page.value = 1;
            await load();
        };

        const goPage = async (p) => {
            const target = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (target === page.value) return;
            page.value = target;
            await load();
        };
        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await load();
        };
        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await load();
        };
        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await load();
        };
        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await load();
        };

        const showFormModal = ref(false);
        const form = ref({
            customer_id: '',
            customer_active_plan_id: '',
            to_let_notice_id: '',
            list_type: 'saved',
        });

        const toasts = ref([]);
        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };
        const removeToast = (id) => {
            toasts.value = toasts.value.filter((x) => x.id !== id);
        };

        const openCreate = () => {
            form.value = { customer_id: '', customer_active_plan_id: '', to_let_notice_id: '', list_type: 'saved' };
            showFormModal.value = true;
        };

        const closeForm = () => {
            if (saving.value) return;
            showFormModal.value = false;
        };

        const save = async () => {
            saving.value = true;
            try {
                const payload = {
                    customer_id: Number(form.value.customer_id || 0),
                    customer_active_plan_id: Number(form.value.customer_active_plan_id || 0),
                    to_let_notice_id: Number(form.value.to_let_notice_id || 0),
                    list_type: form.value.list_type,
                    lang: langValue(),
                };
                const res = await window.axios.post('/admin/api/rent-seeker/shortlist', payload);
                addToast('success', res.data?.message || t('success'));
                showFormModal.value = false;
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                saving.value = false;
            }
        };

        const setListType = async (row, type) => {
            const key = `type_${row.ToLetShortListID}`;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.put(`/admin/api/rent-seeker/shortlist/${row.ToLetShortListID}`, { list_type: type, lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const remove = async (row) => {
            if (!window.confirm(t('confirm_delete'))) return;
            const key = `del_${row.ToLetShortListID}`;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.delete(`/admin/api/rent-seeker/shortlist/${row.ToLetShortListID}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await load();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        onMounted(load);

        return {
            t,
            loading,
            saving,
            busyIds,
            listType,
            searchQuery,
            page,
            perPage,
            pagination,
            items,
            showFilters,
            toggleFilters,
            showColumns,
            columns,
            visibleColumns,
            toggleColumns,
            visibleColumnCount,
            colFilters,
            hasAnyFilter,
            totalPages,
            pageButtons,
            showingText,
            filteredItems,
            listTypeFromRow,
            load,
            debouncedSearch,
            clearFilters,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            showFormModal,
            form,
            openCreate,
            closeForm,
            save,
            setListType,
            remove,
            toasts,
            removeToast,
        };
    },
};
</script>

<style scoped>
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 16px;
}

.page-title {
    font-size: 22px;
    font-weight: 900;
    color: #0f172a;
    margin: 0;
}

.page-subtitle {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
}

.card {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 12px;
    padding: 12px;
}

.datatable-toolbar {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
    background: #ffffff;
    color: #0f172a;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.columns-wrap {
    position: relative;
}

.columns-dropdown {
    position: absolute;
    top: 36px;
    left: 0;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 10px;
    padding: 10px;
    min-width: 180px;
    z-index: 10;
    box-shadow: 0 10px 30px rgba(2, 6, 23, 0.12);
}

.columns-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 6px;
    font-weight: 800;
    font-size: 12px;
    color: #0f172a;
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.data-table th {
    text-align: center;
    padding: 10px 8px;
    color: #ffffff;
    font-weight: 900;
    font-size: 12px;
    background: #0b2c3d;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-top: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.cell-main {
    font-weight: 800;
}

.cell-sub {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
}

.action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
}

.icon-btn {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.icon-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.icon-btn-danger {
    background: #dc2626;
    color: #ffffff;
    border-color: #dc2626;
}

.icon-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    border: 1px solid rgba(15, 23, 42, 0.12);
}

.tag-blue {
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
    border-color: rgba(37, 99, 235, 0.2);
}

.tag-amber {
    background: rgba(245, 158, 11, 0.14);
    color: #92400e;
    border-color: rgba(245, 158, 11, 0.25);
}

.empty {
    text-align: center;
    padding: 24px;
    color: #64748b;
    font-weight: 700;
}

.pager {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding-top: 12px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn,
.pager-num {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    width: min(720px, 92vw);
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    overflow: hidden;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 14px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.modal-body {
    padding: 14px;
}

.modal-footer {
    padding: 14px;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.close-btn {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    cursor: pointer;
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-weight: 900;
    font-size: 12px;
    color: #0f172a;
}

.form-input {
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 10px;
    font-weight: 800;
    font-size: 13px;
    outline: none;
}

.toast-container {
    position: fixed !important;
    top: 20px !important;
    right: 20px !important;
    z-index: 99999 !important;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
}

.toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    min-width: 320px;
    max-width: 420px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
    pointer-events: all;
    position: relative;
    overflow: hidden;
}

.toast::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: currentColor;
}

.toast-success {
    border-left: 4px solid #48bb78;
    color: #22543d;
}

.toast-error {
    border-left: 4px solid #f56565;
    color: #742a2a;
}

.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #2d3748;
}

.toast-close {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0aec0;
    border-radius: 6px;
    transition: all 0.2s;
    padding: 0;
}

.toast-close:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #718096;
}

.toast-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-leave-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-move {
    transition: transform 0.3s ease;
}

@media (max-width: 720px) {
    .form-grid {
        grid-template-columns: 1fr;
    }

    .toast-container {
        top: 10px;
        right: 10px;
        left: 10px;
    }

    .toast {
        min-width: auto;
        max-width: 100%;
    }
}
</style>
